import { Box } from "@rebass/grid";
import {
  isPerformingSubmitActionSelector,
  performingSubmitActionErrorSelector,
} from "@rentiohq/shared-frontend/dist/forms/addContact/schema.addContact.actions";
import * as contactActions from "@rentiohq/shared-frontend/dist/redux/contact/contact.actions";
import * as contactApi from "@rentiohq/shared-frontend/dist/redux/contact/contact.api";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import {
  EMangoPersonType,
  IBrokerKybInfo,
  IBrokerKybSession,
} from "@rentiohq/shared-frontend/dist/types/payment.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { formatDateAndTime } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  DisplayText,
  Error,
  ESpacings,
  Loading,
  Modal,
  MultiStepForm,
} from "@rentiohq/web-shared/dist/components";
import { useInternalMode } from "@rentiohq/web-shared/dist/redux/system/system.hooks";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRootStore } from "redux/reducers";
import kycVerificationSchemas from "../../../../../../forms/kycVerification";
import {
  isMangopayUrl,
  getKybReturnUrl,
  submitActionCreator,
} from "../../../../../../forms/kycVerification/schema.kycVerification.actions";
import * as t from "../../../../../../services/translationService";

interface IProps {
  contactId: number;
  bankAccountId?: number;
  asBroker?: boolean;
  onSuccess: () => void;
  onError: (error: Error) => void;
  onClose: () => void;
}

const CollectionFollowUpKycModal = (props: IProps) => {
  const {
    contactId,
    bankAccountId,
    asBroker = false,
    onSuccess,
    onError,
    onClose,
  } = props;

  // Redux
  const dispatch = useDispatch();

  const isFetching = useSelector((state: IRootStore) =>
    contactId ? contactSelectors.getContactIsFetching(state, contactId) : false,
  );
  const fetchError = useSelector((state: IRootStore) =>
    contactId
      ? contactSelectors.getContactFetchError(state, contactId)
      : undefined,
  );
  const contact = useSelector((state: IRootStore) =>
    contactId ? contactSelectors.getContact(state, contactId) : undefined,
  );
  const meMaster = useSelector((state: IRootStore) =>
    contactId ? contactSelectors.getContactMeMaster(state) : undefined,
  );

  React.useEffect(() => {
    if (!contactId) {
      return;
    }

    dispatch(
      contactActions.getContact.actions.start({
        contactId,
      }),
    );
  }, [contactId]);

  // DEV-2801: companies are legal Mangopay users with a hosted KYB instead of the ID card KYC.
  // Only brokers start KYB; when the check fails the ID card flow stays, as before.
  const [isLegalUser, setIsLegalUser] = React.useState<boolean | undefined>(
    asBroker ? undefined : false,
  );
  const [kybInfo, setKybInfo] = React.useState<IBrokerKybInfo>();

  // Internal mode: hand out the hosted verification link instead of (or before) filling in the form
  const { internalModeEnabled } = useInternalMode();
  const [idvLink, setIdvLink] = React.useState<IBrokerKybSession>();
  const [isRequestingIdvLink, setIsRequestingIdvLink] = React.useState(false);

  const handleRequestIdvLink = async () => {
    setIsRequestingIdvLink(true);

    try {
      const { data } = await contactApi.startKycIdv(contactId);

      if (data.hostedUrl && isMangopayUrl(data.hostedUrl)) {
        setIdvLink(data);
      } else {
        showAlert({
          type: "info",
          message: getLocalizedText(
            "form.kyb_receiver_as_broker.in_progress.title",
          ),
        });
      }
    } catch (error) {
      showAlert({ type: "error", error });
    } finally {
      setIsRequestingIdvLink(false);
    }
  };

  const handleCopyIdvLink = async () => {
    if (!idvLink?.hostedUrl) {
      return;
    }

    try {
      await navigator.clipboard.writeText(idvLink.hostedUrl);
      showAlert({
        type: "success",
        message: getLocalizedText(
          "form.kyc_receiver_as_broker.idv_link.copied",
        ),
      });
    } catch (error) {
      showAlert({ type: "error", error });
    }
  };

  React.useEffect(() => {
    if (!asBroker || !contactId) {
      return;
    }

    let isCancelled = false;

    contactApi
      .getKycIdv(contactId)
      .then(({ data }) => {
        if (!isCancelled) {
          setKybInfo(data);
          setIsLegalUser(data.personType === EMangoPersonType.Legal);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setIsLegalUser(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [asBroker, contactId]);

  // A company read as a private person (its type was not known yet): serverless refuses its ID cards with a 409
  // kyc.error.use_kyb (the alert says why), and the form switches to the KYB with the company's info
  const isMounted = React.useRef(true);
  React.useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const handleError = (error: Error) => {
    const { response } = error as {
      response?: { status?: number; data?: { message?: string } };
    };
    if (
      asBroker &&
      response?.status === 409 &&
      response.data?.message === "kyc.error.use_kyb"
    ) {
      contactApi
        .getKycIdv(contactId)
        .then(({ data }) => {
          if (isMounted.current) {
            setKybInfo(data);
            setIsLegalUser(true);
          }
        })
        // Without the company's info the KYB form would be half empty: report the 409 as before instead
        .catch(() => onError(error));

      return;
    }

    onError(error);
  };

  // Form
  const [formId] = React.useState<number>(generateFormId());

  // Render
  if (!contact || isLegalUser === undefined) {
    if (isFetching || (contact && isLegalUser === undefined)) {
      return (
        <Modal onClose={onClose}>
          <Loading />
        </Modal>
      );
    }

    if (fetchError) {
      return (
        <Modal onClose={onClose}>
          <Error errors={[fetchError]} />
        </Modal>
      );
    }

    return null;
  }

  if (
    contact.id === meMaster?.id &&
    (!meMaster.firstname || !meMaster.lastname)
  ) {
    return (
      <Modal
        onClose={onClose}
        actions={[
          {
            content: getLocalizedText("system.cancel"),
            onClick: onClose,
            appearance: "outline",
            size: "small",
          },
          {
            content: getLocalizedText(
              "form.kyc_receiver_as_broker.incomplete_broker.alert.cta.complete",
            ),
            url: "/company/contact",
            appearance: "primary",
            size: "small",
          },
        ]}
        components={{
          Header: () => (
            <Box pt={ESpacings.loose} px={ESpacings.loose}>
              <DisplayText size="medium">
                {getLocalizedText(
                  "form.kyc_receiver_as_broker.incomplete_broker.alert.title",
                  {
                    name: meMaster.company || getLocalizedText("system.broker"),
                  },
                )}
              </DisplayText>
            </Box>
          ),
        }}
        hasDismiss={true}
        shouldCloseOnOverlayClick={true}
        width="small"
      >
        {getLocalizedText(
          "form.kyc_receiver_as_broker.incomplete_broker.alert.info",
        )}
      </Modal>
    );
  }

  if (idvLink?.hostedUrl) {
    return (
      <Modal
        onClose={onClose}
        heading={getLocalizedText(
          "form.kyc_receiver_as_broker.idv_link.title",
          {
            name: kybInfo?.companyName || contact.company || getName(contact),
          },
        )}
        actions={[
          {
            content: getLocalizedText(
              "form.kyc_receiver_as_broker.idv_link.copy",
            ),
            onClick: handleCopyIdvLink,
            appearance: "outline",
            size: "small",
          },
          {
            content: getLocalizedText(
              "form.kyc_receiver_as_broker.idv_link.open",
            ),
            onClick: () =>
              window.open(
                idvLink.hostedUrl as string,
                "_blank",
                "noopener,noreferrer",
              ),
            appearance: "primary",
            size: "small",
          },
        ]}
        hasDismiss={true}
        shouldCloseOnOverlayClick={true}
        width="medium"
      >
        <Box mb={ESpacings.base} style={{ wordBreak: "break-all" }}>
          {idvLink.hostedUrl}
        </Box>
        {idvLink.expiresAt &&
          getLocalizedText("form.kyc_receiver_as_broker.idv_link.info", {
            date: formatDateAndTime(new Date(idvLink.expiresAt)),
          })}
      </Modal>
    );
  }

  return (
    <MultiStepForm
      formId={`kyc-${formId}`}
      schemas={kycVerificationSchemas({ contact, isLegalUser, kybInfo })}
      asModal={true}
      withAside={false}
      // A KYB continues on Mangopay's page; SCA only opens once the broker is back (kybCallbackContactId)
      onSuccess={isLegalUser ? onClose : onSuccess}
      onError={handleError}
      modalProps={{
        onClose,
      }}
      // KYB: the main button already opens the same hosted session, whose link can be copied from there
      modalActions={
        internalModeEnabled && asBroker && !isLegalUser
          ? [
              {
                content: getLocalizedText(
                  "form.kyc_receiver_as_broker.idv_link.cta",
                ),
                onClick: handleRequestIdvLink,
                isSubmitting: isRequestingIdvLink,
                appearance: "outline",
                size: "small",
                isInternal: true,
              },
            ]
          : []
      }
      isPerformingSubmitActionSelector={isPerformingSubmitActionSelector(
        contact.id,
      )}
      performingSubmitActionErrorSelector={performingSubmitActionErrorSelector(
        contact.id,
      )}
      submitActionCreator={(formData: any) =>
        submitActionCreator(
          contact.id,
          asBroker,
          bankAccountId,
          isLegalUser,
          getKybReturnUrl(contact.id),
        )(formData)
      }
      submitLabel={
        isLegalUser
          ? getLocalizedText("form.kyb_receiver_as_broker.cta.continue")
          : t.system("next")
      }
    />
  );
};

// eslint-disable-next-line import/no-default-export
export default CollectionFollowUpKycModal;
